import { cleanTheTemplate } from '~/util/commonComputed.js'
import { generateSchema } from '~/util/schema/schema.js'
import { asyncContent, asyncContentMounted } from '~/util/asyncContent.js'
import TemplateDefault from '~/components/TemplateDefault.vue'
import prependRoute from '~/util/prependRoute'
import { cl } from '~/plugins/global.js'
import cleanQuerySelector from '~/util/cleanQuerySelector'
export default {
    components: {
        TemplateDefault,
    },
    data() {
        return {
            page: {},
        }
    },
    computed: {
        currentTemplate() {
            return cleanTheTemplate({
                page: this.page,
                fallbackTemplate: 'Default',
            })
        },
        currentMetaTitle() {
            let defaultMetaTitle = this.localize('defaultMetaTitle')
            // TODO: improve these fallbacks
            if (
                this.page &&
                this.page._type === 'resource' &&
                this.page.title &&
                this.page.title.en
            ) {
                defaultMetaTitle = this.page.title.en + ' - Product Resources'
            }
            return this.page &&
                this.page.seometa &&
                this.page.seometa.metaTitle &&
                this.page.seometa.metaTitle[this.$i18n.locale]
                ? this.page.seometa.metaTitle[this.$i18n.locale]
                : defaultMetaTitle
        },
        currentMetaDescription() {
            const defaultMetaDescription = this.localize(
                'defaultMetaDescription'
            )
            return this.page &&
                this.page.seometa &&
                this.page.seometa.metaDescription &&
                this.page.seometa.metaDescription[this.$i18n.locale]
                ? this.page.seometa.metaDescription[this.$i18n.locale]
                : defaultMetaDescription
        },
        currentCanonicals() {
            const locale = this.$i18n.locale
            const base = process.env.V2_BASE_URL
            const type = this.page && this.page._type ? this.page._type : 'page'
            const slug =
                this.page && this.page.slug && this.page.slug[this.$i18n.locale]
                    ? this.page.slug
                    : {
                          en: { current: '404' },
                          es: { current: '404' },
                      }

            const route = prependRoute({ type, slug })
            // IMPORTANT! Nerfing Spanish here and adding en-US below 6/4/2020 DJH
            // const altLocale = this.$i18n.locale === 'es' ? 'en' : 'es'
            const alternates = [
                // {
                //     rel: 'alternate',
                //     hreflang: altLocale,
                //     href: `${base}${route[altLocale]}`,
                //     hid: `c3-hreflang-${altLocale}`
                // },
                // ALSO IMPORTANT! Self-referential hreflang tags are encouraged by Google
                // https://ahrefs.com/blog/hreflang-tags/
                {
                    hid: `c3-hreflang-${locale}`,
                    rel: 'alternate',
                    hreflang: locale,
                    href: `${base}${route[locale]}`,
                },
                {
                    hid: `c3-hreflang-en-US`,
                    rel: 'alternate',
                    hreflang: 'en-us',
                    href: `${base}${route.en}`,
                },
            ]
            if (
                this.page &&
                this.page.seometa &&
                this.page.seometa.canonical &&
                this.page.seometa.canonical.linkTo &&
                this.page.seometa.canonical.linkTo.slug &&
                this.page.seometa.canonical.linkTo.slug[this.$i18n.locale]
            ) {
                // console.log('CANONICAL!')
                const canonicalRoute = prependRoute({
                    type: this.page.seometa.canonical.linkTo._type,
                    slug: this.page.seometa.canonical.linkTo.slug,
                })
                const canonical = {
                    rel: 'canonical',
                    href: `${base}${canonicalRoute[this.$i18n.locale]}`,
                }
                return [canonical, ...alternates]
            } else {
                const canonical = {
                    rel: 'canonical',
                    href: `${base}${route[locale]}`,
                }
                return [canonical, ...alternates]
            }
        },
        ogImageLink() {
            if (
                this.page &&
                this.page.seometa &&
                this.page.seometa.openGraphImage
            ) {
                const { imageId, imageDir } = this.page.seometa.openGraphImage
                const idString =
                    imageId && imageDir ? `${imageDir}/${imageId}` : imageId
                const validIdString = idString || 'logos/c3controls-icon'
                const src = cl.url(validIdString, {
                    crop: 'fill',
                })
                return src
            } else {
                return ''
            }
        },
        sanityOpenGraphImg() {
            const ogFileName = this.page?.seometa?.sanityImageOG?.selectedImage
                ?.asset?.originalFilename
            return ogFileName
                ? `${process.env.ASSET_HOSTNAME}${ogFileName}`
                : ''
        },
    },
    jsonld() {
        return generateSchema({
            page: this.page,
            lang: this.$i18n.locale,
            route: this.$route.params,
        })
    },
    async asyncData(context) {
        // console.log('asyncData was here at', Date.now())
        // default behavior
        const results = await asyncContent({
            context,
            process,
        })
        return results
    },
    async mounted() {
        // console.log('async mounted was here at', Date.now())

        if (this.$route.query.fresh === '1' || this.$route.query.preview) {
            // console.log('async mounted will run asyncContentMounted...')
            this.page = await asyncContentMounted(this)
            console.log(this.page)
        }
        if (window.location.hash)
            this.$nextTick(() => {
                this.handleScrollToContent()
            })
    },
    head() {
        const i18nSeo = this.$nuxtI18nHead({ addSeoAttributes: true })
        // console.log('i18nSeo', i18nSeo)
        return {
            htmlAttrs: {
                fnord: 'fnord',
                ...i18nSeo.htmlAttrs,
            },
            title: this.currentMetaTitle,
            link: [...this.currentCanonicals],
            // link: [...this.currentCanonicals, ...i18nSeo.link],
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.currentMetaDescription,
                },
                {
                    hid: 'title',
                    name: 'title',
                    content: this.currentMetaTitle,
                },
                {
                    hid: 'og:image',
                    name: 'og:image',
                    content: this.sanityOpenGraphImg,
                },
                {
                    hid: 'og:title',
                    name: 'og:title',
                    content: this.currentMetaTitle,
                },
                {
                    hid: 'og:description',
                    name: 'og:description',
                    content: this.currentMetaDescription,
                },
                ...i18nSeo.meta,
            ],
        }
    },
    methods: {
        handleScrollToContent() {
            const hash = window.location.hash
            const cleanHash = cleanQuerySelector(hash)
            this.$vuetify.goTo(cleanHash, this.scrollOptions)
        },
    },
}
